// Innstillinger → API-nøkler.
//
// Admin oppretter én nøkkel per leverandørintegrasjon. Nøkkelen vises i en
// dialog ÉN gang rett etter opprettelse — backend lagrer bare hashen, så den
// kan aldri hentes fram igjen. Tilbakekalling er endelig; raden blir stående
// som revisjonsspor. Hvordan leverandøren bruker nøkkelen står på
// /api-dokumentasjon.

import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import {
  getVendorApiKeys,
  createVendorApiKey,
  revokeVendorApiKey,
  fetchVendorList,
} from "api/apiService";
import { notifyCenter } from "utils/toast";

const fmtDate = (v) => {
  if (!v) return "–";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "–";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

function VendorApiKeys() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newVendorId, setNewVendorId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);

  // one-time key dialog
  const [freshKey, setFreshKey] = useState(null); // { key, label, vendor_name }

  // revoke confirm
  const [revokeRow, setRevokeRow] = useState(null);
  const [revoking, setRevoking] = useState(false);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await getVendorApiKeys();
      setRows(res?.ok ? res.data || [] : []);
      if (!res?.ok) notifyCenter("error", "Kunne ikke hente API-nøkler", { duration: 3000 });
    } catch {
      notifyCenter("error", "Kunne ikke hente API-nøkler", { duration: 3000 });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    (async () => {
      try {
        // fetch_vendor_list svarer { status, vendor_list: [{ id, title }] }
        const res = await fetchVendorList();
        const list = Array.isArray(res?.vendor_list) ? res.vendor_list : [];
        setVendors(list.map((v) => ({ id: v.id, name: v.title })));
      } catch {
        setVendors([]);
      }
    })();
  }, []);

  async function handleCreate() {
    if (!newVendorId || !newLabel.trim()) {
      notifyCenter("error", "Velg leverandør og gi nøkkelen et navn.", { duration: 3000 });
      return;
    }
    setCreating(true);
    try {
      const res = await createVendorApiKey({ vendor_id: Number(newVendorId), label: newLabel.trim() });
      if (res?.ok && res?.key) {
        setCreateOpen(false);
        setNewLabel("");
        setNewVendorId("");
        setFreshKey({ key: res.key, label: res.data?.label, vendor_name: res.data?.vendor_name });
        fetchData();
      } else {
        notifyCenter("error", `Kunne ikke opprette nøkkel: ${res?.error || "ukjent feil"}`, { duration: 4000 });
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke opprette nøkkel", { duration: 4000 });
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    if (!revokeRow) return;
    setRevoking(true);
    try {
      const res = await revokeVendorApiKey(revokeRow.id);
      if (res?.ok) {
        notifyCenter("success", "Nøkkelen er tilbakekalt.", { duration: 3000 });
        setRevokeRow(null);
        fetchData();
      } else {
        notifyCenter("error", `Kunne ikke tilbakekalle: ${res?.error || "ukjent feil"}`, { duration: 4000 });
      }
    } catch {
      notifyCenter("error", "Kunne ikke tilbakekalle nøkkelen", { duration: 4000 });
    } finally {
      setRevoking(false);
    }
  }

  function copyKey() {
    if (!freshKey?.key) return;
    try {
      navigator.clipboard.writeText(freshKey.key);
      notifyCenter("success", "Nøkkelen er kopiert.", { duration: 2000 });
    } catch {
      notifyCenter("error", "Kunne ikke kopiere — merk teksten manuelt.", { duration: 3000 });
    }
  }

  return (
    <DashboardLayout>
      <SoftBox py={3}>
        <Card sx={{ p: 3 }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                API-nøkler for leverandører
              </SoftTypography>
              <SoftTypography variant="caption" color="text" textTransform="uppercase">
                Nøkkelen vises bare én gang ved opprettelse — lagre den trygt hos leverandøren.
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" gap={1}>
              <SoftButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<MenuBookOutlinedIcon />}
                onClick={() => navigate("/api-dokumentasjon")}
              >
                API-dokumentasjon
              </SoftButton>
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Ny nøkkel
              </SoftButton>
            </SoftBox>
          </SoftBox>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </SoftBox>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Navn</TableCell>
                    <TableCell>Leverandør</TableCell>
                    <TableCell>Nøkkel</TableCell>
                    <TableCell>Opprettet</TableCell>
                    <TableCell>Sist brukt</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <SoftTypography variant="body2" color="text" py={2} textAlign="center">
                          Ingen API-nøkler ennå. Opprett én per leverandørintegrasjon.
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell>{r.vendor_name}</TableCell>
                      <TableCell>
                        <code>{r.prefix}</code>
                      </TableCell>
                      <TableCell>{fmtDate(r.created_at)}</TableCell>
                      <TableCell>{fmtDate(r.last_used_at)}</TableCell>
                      <TableCell>
                        {r.revoked_at ? (
                          <Chip size="small" color="error" label={`Tilbakekalt ${fmtDate(r.revoked_at)}`} />
                        ) : (
                          <Chip size="small" color="success" label="Aktiv" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!r.revoked_at && (
                          <Tooltip title="Tilbakekall nøkkelen">
                            <IconButton size="small" color="error" onClick={() => setRevokeRow(r)}>
                              <BlockOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>

      {/* Opprett nøkkel */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ny API-nøkkel</DialogTitle>
        <DialogContent>
          <SoftBox display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <SoftTypography variant="caption" fontWeight="bold" mb={0.5}>
                Leverandør
              </SoftTypography>
              <Select
                value={newVendorId}
                onChange={(e) => setNewVendorId(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="" disabled>
                  Velg leverandør…
                </MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" mb={0.5} display="block">
                Navn (hva nøkkelen brukes til)
              </SoftTypography>
              <TextField
                fullWidth
                size="small"
                placeholder="F.eks. ERP-integrasjon"
                value={newLabel}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
            Avbryt
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleCreate} disabled={creating}>
            {creating ? "Oppretter…" : "Opprett"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Vis nøkkelen én gang */}
      <Dialog open={!!freshKey} fullWidth maxWidth="sm">
        <DialogTitle>Nøkkelen er opprettet</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" mb={2}>
            Kopier nøkkelen nå og send den trygt til <b>{freshKey?.vendor_name}</b>. Den vises{" "}
            <b>aldri igjen</b> — mister leverandøren den, må du tilbakekalle denne og opprette en ny.
          </SoftTypography>
          <SoftBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.5}
            sx={{ background: "#f4f6f9", borderRadius: 2, border: "1px dashed #c6ccd4" }}
          >
            <code style={{ flex: 1, wordBreak: "break-all", fontSize: 14 }}>{freshKey?.key}</code>
            <Tooltip title="Kopier">
              <IconButton size="small" onClick={copyKey}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </SoftBox>
          <SoftTypography variant="caption" color="text" mt={2} display="block">
            Leverandøren sender nøkkelen som HTTP-headeren <code>X-Bmax-Api-Key</code> — se{" "}
            <a href="/api-dokumentasjon">API-dokumentasjonen</a>.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="info" onClick={() => setFreshKey(null)}>
            Jeg har lagret nøkkelen
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Tilbakekall */}
      <Dialog open={!!revokeRow} onClose={() => !revoking && setRevokeRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Tilbakekall nøkkel?</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            «{revokeRow?.label}» for {revokeRow?.vendor_name} slutter å virke umiddelbart. Dette kan
            ikke angres — integrasjonen trenger i så fall en ny nøkkel.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setRevokeRow(null)} disabled={revoking}>
            Avbryt
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? "Tilbakekaller…" : "Tilbakekall"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default VendorApiKeys;
